import type { SttPort, SttRecordingResult } from "../../ports/stt.port";

// MediaRecorder STT-адаптер.
//
// Воркфлоу: (prewarm на pointerdown) → startRecording() → stopRecording().
//
// Латентность старта. getUserMedia инициализирует устройство ~300-800мс на
// mobile. Раньше делали ДВА getUserMedia подряд (requestPermission + startRecording)
// → отсюда видимая ~1с задержка до старта записи. Теперь:
//   • requestPermission() НЕ трогает устройство (Permissions API) — только рано
//     отсекает явный 'denied'. Единственный getUserMedia делает startRecording().
//   • prewarm() (на pointerdown, если доступ уже выдан) греет поток заранее, чтобы
//     startRecording() стартовал мгновенно и не терял первые слова пользователя.
//
// Тишину НЕ детектим на клиенте (ложные срабатывания: шум при молчании / шёпот в
// тишине). На тишине полагаемся на промпт STT (модель возвращает "") + серверную
// чистку cue-таймкодов (modules/tutor/_internal/sanitize-transcript).

let mediaStream: MediaStream | null = null;
let mediaRecorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let startedAt = 0;

// --- Прогрев микрофона (prewarm) ---
let warmStream: MediaStream | null = null;
let warming = false;
let warmExpiry: ReturnType<typeof setTimeout> | undefined;
/** Прогретый поток живёт ограниченно — иначе индикатор записи горит зря. */
const WARM_TTL_MS = 3000;

function pickMimeType(): string {
	if (typeof MediaRecorder === "undefined") return "audio/webm";
	const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
	for (const mt of candidates) {
		if (MediaRecorder.isTypeSupported?.(mt)) return mt;
	}
	return "audio/webm";
}

function streamIsLive(s: MediaStream | null): s is MediaStream {
	return !!s && s.getAudioTracks().some((t) => t.readyState === "live");
}

function stopStream(s: MediaStream | null): void {
	if (s) for (const t of s.getTracks()) t.stop();
}

function dropWarmStream(): void {
	clearTimeout(warmExpiry);
	warmExpiry = undefined;
	stopStream(warmStream);
	warmStream = null;
}

async function acquireStream(): Promise<MediaStream> {
	clearTimeout(warmExpiry);
	warmExpiry = undefined;
	// Готовый прогретый поток — отдаём мгновенно (передаём владение рекордеру).
	if (streamIsLive(warmStream)) {
		const s = warmStream;
		warmStream = null;
		return s;
	}
	// Холодный старт — единственный getUserMedia.
	return navigator.mediaDevices.getUserMedia({ audio: true });
}

export const sttWebRecorderAdapter: SttPort = {
	isSupported() {
		return (
			typeof navigator !== "undefined" &&
			!!navigator.mediaDevices?.getUserMedia &&
			typeof MediaRecorder !== "undefined"
		);
	},

	prewarm() {
		if (!this.isSupported() || warming || streamIsLive(warmStream) || mediaRecorder) return;
		warming = true;
		void (async () => {
			try {
				// Греем ТОЛЬКО при уже выданном доступе — иначе prompt всплыл бы на
				// pointerdown (в т.ч. в браузерах без Permissions API). Холодный путь
				// по клику сам запросит разрешение.
				const perm = await navigator.permissions
					?.query({ name: "microphone" as PermissionName })
					.catch(() => null);
				if (perm?.state !== "granted") {
					warming = false;
					return;
				}
				const s = await navigator.mediaDevices.getUserMedia({ audio: true });
				warming = false;
				// Пока грелись — запись могла стартовать холодным путём: поток лишний.
				if (mediaRecorder || streamIsLive(warmStream)) {
					stopStream(s);
					return;
				}
				warmStream = s;
				clearTimeout(warmExpiry);
				warmExpiry = setTimeout(dropWarmStream, WARM_TTL_MS);
			} catch {
				warming = false;
			}
		})();
	},

	async requestPermission() {
		if (!this.isSupported()) return "denied";
		// Лёгкая проверка БЕЗ инициализации устройства (в отличие от getUserMedia).
		// Реальный prompt + единственный getUserMedia делает startRecording().
		try {
			const perm = await navigator.permissions?.query({
				name: "microphone" as PermissionName,
			});
			if (perm?.state === "denied") return "denied";
		} catch {
			// Permissions API без 'microphone' (часть Firefox/Safari) — не блокируем.
		}
		return "granted";
	},

	async startRecording() {
		if (!this.isSupported()) {
			throw new Error("STT не поддерживается в данном окружении");
		}
		if (mediaRecorder?.state === "recording") return;

		mediaStream = await acquireStream();
		const mimeType = pickMimeType();
		mediaRecorder = new MediaRecorder(mediaStream, { mimeType });
		chunks = [];
		startedAt = performance.now();

		mediaRecorder.ondataavailable = (e) => {
			if (e.data && e.data.size > 0) chunks.push(e.data);
		};
		mediaRecorder.start();
	},

	async stopRecording(): Promise<SttRecordingResult> {
		if (!mediaRecorder) throw new Error("Запись не была начата");
		const mimeType = mediaRecorder.mimeType || "audio/webm";

		return new Promise((resolve) => {
			const rec = mediaRecorder;
			if (!rec) throw new Error("MediaRecorder исчез");
			rec.onstop = () => {
				const blob = new Blob(chunks, { type: mimeType });
				const durationMs = Math.round(performance.now() - startedAt);
				// Освобождаем микрофон.
				if (mediaStream) {
					stopStream(mediaStream);
					mediaStream = null;
				}
				mediaRecorder = null;
				chunks = [];
				resolve({ blob, mimeType, durationMs });
			};
			rec.stop();
		});
	},

	async cancelRecording() {
		if (mediaRecorder?.state === "recording") {
			mediaRecorder.stop();
		}
		if (mediaStream) {
			stopStream(mediaStream);
			mediaStream = null;
		}
		mediaRecorder = null;
		chunks = [];
		dropWarmStream();
	},

	isRecording() {
		return mediaRecorder?.state === "recording";
	},
};
